import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { companyDetails } from '../lib/mock';
import { MapPin, Phone, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import SEO from '../components/SEO';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Send to backend
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.status === 200 || response.status === 201) {
        toast.success("Message sent successfully!", {
          description: "We will get back to you shortly."
        });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message.", {
        description: "Please try again or call us directly."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      <SEO 
        title="Contact Us - Get a Free Water Treatment Plant Quote"
        description="Contact BG TECHNO CHEM for expert STP, ETP, WTP & RO plant solutions. Call +91 98142 04405 or visit S.C.O. 26, 27, 28, Bank Colony Road, Jamalpur Chowk, Ludhiana, Punjab."
        keywords="contact BG Techno Chem, water treatment plant quote, STP ETP plant Ludhiana contact, water treatment company near me"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bgtechnochem.com/" },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://bgtechnochem.com/contact" }
          ]
        }}
      />
      
      {/* Header */}
      <section className="bg-primary py-16 text-center text-white">
        <h1 className="text-4xl font-bold mb-4">Contact Us</h1>
        <p className="text-blue-100 max-w-xl mx-auto">
          Get in touch for quotes, consultations, or technical support. We are here to help.
        </p>
      </section>

      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          
          {/* Contact Information */}
          <div className="space-y-8">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>Get In Touch</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Head Office</h3>
                    <p className="text-slate-600">{companyDetails.location}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Phone className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Phone</h3>
                    <p className="text-slate-600">{companyDetails.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 text-primary mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">Email</h3>
                    <p className="text-slate-600">{companyDetails.email}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Google Map Embed */}
            <div className="h-[300px] w-full rounded-2xl overflow-hidden shadow-lg border border-slate-200">
              <iframe 
                src="https://maps.google.com/maps?q=Satyam%20Clinic%2C%20Jamalpur%20Chowk%2C%20Ludhiana%2C%20Punjab&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%" 
                height="100%" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-none shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl">Send us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      data-testid="contact-name-input"
                      placeholder="John Doe" 
                      required 
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      data-testid="contact-phone-input"
                      placeholder="+91..." 
                      required 
                      value={formData.phone}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input 
                    id="email" 
                    name="email" 
                    data-testid="contact-email-input"
                    type="email" 
                    placeholder="john@company.com" 
                    required 
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input 
                    id="subject" 
                    name="subject" 
                    data-testid="contact-subject-input"
                    placeholder="Inquiry about STP..." 
                    required 
                    value={formData.subject}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea 
                    id="message" 
                    name="message" 
                    data-testid="contact-message-input"
                    placeholder="Tell us about your project requirements..." 
                    className="min-h-[150px]" 
                    required 
                    value={formData.message}
                    onChange={handleChange}
                  />
                </div>

                <Button type="submit" data-testid="contact-submit-button" className="w-full bg-primary hover:bg-brand-darkBlue h-12 text-lg" disabled={loading}>
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
}
